import React, { useState } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'antd';
import { ArrowForwardOutlined, Create, StarRounded } from '@material-ui/icons';
import {
  updateTemplate,
  getTemplateBy_id,
  copyTemplate,
  copySharedTemplate,
} from '../../../../redux/services';
import { fullloader, toast } from '../../../../redux/action';
import { Card, CardHeader, Button, ButtonGroup } from '../_CardUI/Card.styled';
import {
  FavoriteButton,
  ArchiveButton,
  UnarchiveButton,
  CopyButton,
} from '../_CardUI/CardActionButtons.styled';
import { formatDate, trimTextIfNeeded, formatTimestampToDate } from '../_utils';
import Thumbnail from '../Thumbnail';
import CopyModal from '../CopyModal';

const UserTemplateCard = ({
  template,
  archive,
  unarchive,
  onClick,
  reloadTemplates,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [isCopyModalOpen, setCopyModalOpen] = useState(false);
  const [templateCopyName, setTemplateCopyName] = useState('');
  const [newOrgId, setNewOrgId] = useState('');
  const [isCreatingCopy, setCreatingCopy] = useState(false);

  const applyTemplateToDraft = queryString.parse(location.search).apply === '1';

  const [starred, setStarred] = useState(template.starred);
  const starredTitle = starred ? 'Remove from Favorites' : 'Add to Favorites';

  const openCopyModal = () => {
    setCopyModalOpen(true);
  };

  const closeCopyModal = () => {
    setCopyModalOpen(false);
  };

  const toggleFavoriteButton = async isFavorite => {
    try {
      await updateTemplate({ ...template, starred: isFavorite });
      let text = '';
      if (isFavorite) {
        setStarred(true);
        text = 'added to';
      } else {
        setStarred(false);
        text = 'removed from';
      }
      dispatch(toast('success', `Template ${text} Favorites`));
    } catch (e) {
      dispatch(toast('error', `Failed to update template. ${e.message}`));
    }
  };

  const goToEdit = async () => {
    try {
      const templateData = await getTemplateBy_id(template._id);
      history.push(`/dashboard/sendmessage?template=${templateData._id}`);
    } catch (e) {
      dispatch(toast('error', 'Failed to fetch template'));
    }
  };

  const createTemplateCopy = async () => {
    dispatch(fullloader(true, 'Creating a copy of the template'));
    setCreatingCopy(true);

    try {
      await copyTemplate({
        _id: template._id,
        name: templateCopyName || `Copy of ${template.name}`,
        newOrgId,
      });
      dispatch(toast('success', 'Template copied.'));
      closeCopyModal();
      reloadTemplates();
    } catch (e) {
      dispatch(toast('error', 'Error copying the template'));
    } finally {
      setCreatingCopy(false);
      dispatch(fullloader(false));
    }
  };

  const createSharedTemplateCopy = async () => {
    dispatch(fullloader(true, 'Creating a shared template'));
    setCreatingCopy(true);

    try {
      await copySharedTemplate(
        template._id,
        templateCopyName || `Copy of ${template.name}`
      );
      dispatch(toast('success', 'Shared Template created.'));
      closeCopyModal();
      reloadTemplates();
    } catch (e) {
      dispatch(toast('error', 'Error creating the shared template'));
    } finally {
      setCreatingCopy(false);
      dispatch(fullloader(false));
    }
  };

  const getActionIcons = () => {
    if (applyTemplateToDraft) return '';

    return (
      <>
        {template.archived ? (
          <>
            <CardHeader.Left />
            <CardHeader.Right>
              <UnarchiveButton onClick={unarchive} />
            </CardHeader.Right>
          </>
        ) : (
          <>
            <CardHeader.Left />
            <CardHeader.Right>
              <Tooltip title={starredTitle}>
                <FavoriteButton
                  as={StarRounded}
                  $starred={starred ? true : false}
                  onClick={() => {
                    if (starred) {
                      toggleFavoriteButton(false);
                    } else {
                      toggleFavoriteButton(true);
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title="Duplicate">
                <CopyButton onClick={openCopyModal} />
              </Tooltip>
              <Tooltip title="Archive">
                <ArchiveButton onClick={archive} />
              </Tooltip>
            </CardHeader.Right>
          </>
        )}
      </>
    );
  };

  return (
    <Card>
      <Thumbnail html={template.html} />
      <Card.Body>
        <CardHeader>{getActionIcons()}</CardHeader>
        <Tooltip
          title={`Create a new Draft with Template: "${template.name}"`}
        />
        <Card.Name>{trimTextIfNeeded(template.name, 60)}</Card.Name>
        {template.subject ? (
          <Card.Subject>
            Subject: {trimTextIfNeeded(template.subject, 54)}
          </Card.Subject>
        ) : null}
        <Card.Date>
          Created: {formatTimestampToDate(template.createdAt)} <br />
          Last Updated: {formatDate(template.saveTime)}
        </Card.Date>
        <ButtonGroup>
          <Tooltip title="Opens this template for editing">
            <Button onClick={goToEdit} light>
              Edit <Create style={{ fontSize: '14px' }} />
            </Button>
          </Tooltip>
          <Tooltip title="Opens a new draft using this template">
            <Button onClick={onClick}>
              Use <ArrowForwardOutlined style={{ fontSize: '14px' }} />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Card.Body>
      {isCopyModalOpen && (
        <CopyModal
          isCreatingCopy={isCreatingCopy}
          setCopyName={setTemplateCopyName}
          copyName={templateCopyName}
          createCopy={createTemplateCopy}
          closeCopyModal={closeCopyModal}
          setNewOrgId={setNewOrgId}
          createSharedTemplateCopy={createSharedTemplateCopy}
        />
      )}
    </Card>
  );
};

export default UserTemplateCard;
